import { useEffect, useState } from "react";

import { Modal } from "@/components/Modal";

interface Hotkey {
  combo: string;
  scope: string;
  action: string;
}

const HOTKEYS: Hotkey[] = [
  // Global
  { combo: "?", scope: "Global", action: "Open this hotkey reference" },
  { combo: "Ctrl/⌘ + K", scope: "Global", action: "Open command palette" },
  { combo: "Esc", scope: "Global", action: "Close modals / blur inputs" },
  // Order ticket
  { combo: "B", scope: "Order ticket", action: "Set side to BUY" },
  { combo: "S", scope: "Order ticket", action: "Set side to SELL" },
  { combo: "Enter", scope: "Order ticket", action: "Submit order" },
  // Watchlist
  { combo: "Click row", scope: "Watchlist", action: "Switch active symbol" },
  { combo: "Enter", scope: "Watchlist", action: "Add typed symbol to list" },
  // Terminal
  { combo: "Drag pane header", scope: "Terminal", action: "Reflow grid layout" },
  // Account
  { combo: "Navbar dropdown", scope: "Account", action: "Switch active trading account" },
];


/**
 * Press `?` anywhere (outside an input) to open the modal listing every
 * hotkey + slash command. Mounted once at the layout level so it works
 * on every page.
 */
export function KeyboardHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Don't fire inside text inputs / textareas / contenteditables —
      // the user is typing a `?`, not asking for help.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
        return;
      }
      if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setOpen((cur) => !cur);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Modal open={open} onClose={() => setOpen(false)} title="Keyboard reference">
      <div className="space-y-2 text-xs">
        <p className="text-term-mute">
          Press <kbd className="rounded-sm bg-term-elev px-1">?</kbd> to toggle.
        </p>
        <table className="w-full">
          <thead className="text-term-mute">
            <tr>
              <th className="py-1 text-left text-2xs uppercase tracking-wider">Combo</th>
              <th className="py-1 text-left text-2xs uppercase tracking-wider">Scope</th>
              <th className="py-1 text-left text-2xs uppercase tracking-wider">Action</th>
            </tr>
          </thead>
          <tbody>
            {HOTKEYS.map((h) => (
              <tr key={`${h.combo}-${h.scope}`} className="border-t border-term-border">
                <td className="py-1">
                  <kbd className="rounded-sm bg-term-elev px-1.5 py-0.5 font-mono text-2xs">{h.combo}</kbd>
                </td>
                <td className="py-1 text-term-dim">{h.scope}</td>
                <td className="py-1 text-term-fg">{h.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
